<script setup>
// The player sheet behind a PlayerCard (mirrors the Flutter PlayerDialog
// bottom sheet): contact details, Stats, Message / Unblock / Follow, and
// Impersonate for a super admin.
//
// Its own component, mounted by the card only once it has been opened, so the
// messaging, safety and follow composables (and the auth service) exist for a
// tapped card and not for every row of a members list: a signed-in club page
// renders 800 cards and none of this is reachable until a tap (MEMORY_M4,
// 2026-10-20). Everything it shows comes from the card: `player` is the card's
// currentItem (the full record once fetched, else the row it was given).
import {computed, ref, watch} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {useMessaging} from '~/composables/useMessaging';
import SquashLevelsChip from '~/components/squashLevels/SquashLevelsChip.vue';
import SquashLevelsStatusRow from '~/components/squashLevels/SquashLevelsStatusRow.vue';
import {withSquashLevelsSharing} from '~/composables/useSquashLevelsCards';
import AvatarImage from '~/components/images/AvatarImage.vue';
import {useValidation} from '~/composables/useValidation';
import {HIDDEN_CONTACTS_NOTE, UNBLOCK_TO_MESSAGE_LABEL, messageActionFor} from '~/composables/useMessagePermission.js';
import {useSafety} from '~/composables/useSafety';
import {useVisitorPreview} from '~/composables/useVisitorPreview';
import {FOLLOW_LABELS, useFollows} from '~/composables/useFollows';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  // The card's currentItem: the player this sheet is about.
  player: {
    type: Object,
    default: () => null,
  },
});
const emit = defineEmits(['update:modelValue']);

const {isGuid} = useValidation();
const {user, impersonateUser, impersonateLoading, isSuperUser, isAuthenticated} = useAuthService();
// X73: in a club page's visitor preview the sheet is a visitor's: no Message / Follow / blocked list.
const visitorPreview = useVisitorPreview();
const signedIn = computed(() => isAuthenticated.value && !visitorPreview.value);
const {createConversation} = useMessaging();
const {blockedUserIds, loadBlocked, unblock} = useSafety();
const router = useRouter();
const route = useRoute();
const messageLoading = ref(false);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});
const currentItem = computed(() => props.player);

// Squash Levels (uat, 3 Nov 2026): the sheet draws whatever the API sent for this
// player. Your own change, once the API took it, shows here at once; the next
// load of the player brings the API's own answer back.
const sharingOverride = ref(null);
const sheetPerson = computed(() => withSquashLevelsSharing(currentItem.value, sharingOverride.value));
watch(() => currentItem.value?.id, () => { sharingOverride.value = null; });
const onSharingChanged = (status) => {
  sharingOverride.value = status;
  // the app-wide listener service (auto-imported), asked only now: other cards and the profile follow the change
  useListenerService().notify('squashLevelsSharingChanged', status);
};

// Who the viewer has blocked decides between Message and Unblock to
// message. Loaded once per session (cached), each time the sheet opens.
watch(() => props.modelValue, (isOpen) => {
  if (isOpen && signedIn.value) loadBlocked();
}, {immediate: true});

const isMe = computed(() =>
    signedIn.value && isGuid(currentItem.value?.userId) && currentItem.value?.userId === user.value?.id);
// Never read off a hidden player's stub: it carries no account either way.
const adminManaged = computed(() =>
    !currentItem.value?.obfuscated && !isGuid(currentItem.value?.userId) && !currentItem.value?.hasInvite);
const playerHasInvite = computed(() => !!currentItem.value?.hasInvite);
const hasEmail = computed(() => !!(currentItem.value?.email && currentItem.value.email.length > 1));
const hasPhone = computed(() => !!(currentItem.value?.telephone && currentItem.value.telephone.length > 1));
// Whether the viewer may start a chat: the API's canMessage, NOT "not
// obfuscated", which only hides email and phone (release fixes, 28 Sep 2026).
// Someone the viewer blocked gets "Unblock to message" instead (29 Sep 2026).
const messageAction = computed(() =>
    messageActionFor(currentItem.value, {
      signedIn: signedIn.value, myUserId: user.value?.id, blockedUserIds: blockedUserIds.value,
    }));

// Unblock, then start the chat as Message would. A refusal (they are no longer
// in a club you share, say) is shown by createConversation and nothing opens.
const handleUnblockToMessage = async () => {
  if (!currentItem.value?.userId) return;
  try {
    messageLoading.value = true;
    await unblock(currentItem.value.userId);
  } catch (error) {
    messageLoading.value = false;
    return;
  }
  await handleMessage();
};

// Follow (PRIVACY_PLAN.md §3.6): only for someone the viewer shares no club with,
// and only between adults; the API's followStatus says which, per viewer.
const {request: requestFollow, accept: acceptFollow, remove: removeFollow, list: listFollows, reasonOf: followReason} = useFollows();
const followBusy = ref(false);
const followStatus = computed(() => currentItem.value?.followStatus ?? null);
const showFollow = computed(() => ['none', 'requested', 'requestReceived', 'following'].includes(followStatus.value));
const followLabel = computed(() => FOLLOW_LABELS[followStatus.value] ?? 'Follow');
const handleFollow = async () => {
  if (followBusy.value || !currentItem.value) return;
  followBusy.value = true;
  try {
    if (followStatus.value === 'requestReceived') {
      const mine = await listFollows();
      const received = (mine || []).find(f => f.direction === 'received' && f.status === 0 && f.person?.playerId === currentItem.value.id);
      if (received) await acceptFollow(received.id);
      currentItem.value.followStatus = 'following';
    } else if (followStatus.value === 'none') {
      await requestFollow(currentItem.value.id);
      currentItem.value.followStatus = 'requested';
    } else if (followStatus.value === 'requested') {
      const mine = await listFollows();
      const sent = (mine || []).find(f => f.direction === 'sent' && f.status === 0 && f.person?.playerId === currentItem.value.id);
      if (sent) await removeFollow(sent.id);
      currentItem.value.followStatus = 'none';
    }
  } catch (e) {
    showError(followReason(e));
  } finally {
    followBusy.value = false;
  }
};

const handleMessage = async () => {
  if (!currentItem.value?.userId) return;

  try {
    messageLoading.value = true;
    const conversation = await createConversation([currentItem.value.userId], 1, null);
    router.push({
      query: {...route.query, conversationId: conversation.id, messaging: 'open'}
    });
    open.value = false;
  } catch (error) {
    console.error('Failed to create conversation:', error);
  } finally {
    messageLoading.value = false;
  }
};
</script>

<template>
  <v-dialog v-model="open" max-width="440">
    <div class="tl-sheet" v-if="currentItem">
      <!-- head: close only -->
      <div class="tl-sheet-head">
        <v-btn :icon="true" variant="text" size="small" @click="open = false">
          <v-icon icon="mdi-close"/>
        </v-btn>
      </div>

      <!-- centred identity -->
      <div class="tl-sheet-id">
        <avatar-image :size="76" :filename="currentItem.imageFileName" :fallback="currentItem.initials"/>
        <div class="tl-sheet-name">{{ currentItem.fullName || 'Player' }}</div>
        <!-- the sheet draws whatever the API sent for this player; your own change shows at once -->
        <div v-if="sheetPerson" class="mt-1 d-flex justify-center">
          <squash-levels-chip :person="sheetPerson" :allowed="true"/>
        </div>
        <div v-if="adminManaged || playerHasInvite || isMe" class="d-flex justify-center ga-2 mt-3">
          <span v-if="playerHasInvite" class="tl-chip tl-chip--ok">Awaiting invite acceptance</span>
          <span v-else-if="adminManaged" class="tl-chip tl-chip--warn">Admin managed</span>
          <span v-if="isMe" class="tl-chip tl-chip--brand">Your account</span>
        </div>
      </div>

      <!-- Squash Levels: one line; your own sheet can change it in place -->
      <div v-if="sheetPerson?.squashLevelsSharing" class="tl-sheet-body pb-0">
        <squash-levels-status-row :person="sheetPerson" :is-me="isMe" @changed="onSharingChanged"/>
      </div>

      <!-- contact card -->
      <div v-if="hasEmail || hasPhone" class="tl-sheet-body">
        <div class="tl-contact-card">
          <a v-if="hasEmail" class="tl-contact-row" :href="currentItem.obfuscated ? undefined : 'mailto:' + currentItem.email">
            <div class="tl-contact-tile"><v-icon size="21">mdi-email-outline</v-icon></div>
            <div class="tl-contact-text">
              <div class="tl-contact-value" :class="{ 'tl-contact-value--locked': currentItem.obfuscated }">{{ currentItem.email }}</div>
              <div v-if="!currentItem.obfuscated" class="tl-contact-hint">Tap to email</div>
            </div>
            <v-icon v-if="currentItem.obfuscated" size="18" color="textSubtle">mdi-lock-outline</v-icon>
          </a>
          <a v-if="hasPhone" class="tl-contact-row" :href="currentItem.obfuscated ? undefined : 'tel:' + currentItem.telephone">
            <div class="tl-contact-tile"><v-icon size="21">mdi-phone-outline</v-icon></div>
            <div class="tl-contact-text">
              <div class="tl-contact-value" :class="{ 'tl-contact-value--locked': currentItem.obfuscated }">{{ currentItem.telephone }}</div>
              <div v-if="!currentItem.obfuscated" class="tl-contact-hint">Tap to call</div>
            </div>
            <v-icon v-if="currentItem.obfuscated" size="18" color="textSubtle">mdi-lock-outline</v-icon>
          </a>
        </div>
        <div v-if="currentItem.obfuscated" class="tl-sheet-note mt-2">
          {{ HIDDEN_CONTACTS_NOTE }}
        </div>
      </div>

      <!-- foot: Stats | Message -->
      <div class="tl-sheet-foot">
        <v-btn
            variant="tonal"
            color="primary"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-chart-line"
            :to="{ name: 'PlayerStats', params: { playerId: currentItem.id } }"
        >Stats</v-btn>
        <v-btn
            v-if="messageAction === 'message'"
            color="primary"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-chat-outline"
            :loading="messageLoading"
            @click="handleMessage"
        >Message</v-btn>
        <v-btn
            v-else-if="messageAction === 'unblock'"
            color="primary"
            variant="tonal"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-account-lock-open-outline"
            :loading="messageLoading"
            @click="handleUnblockToMessage"
        >{{ UNBLOCK_TO_MESSAGE_LABEL }}</v-btn>
        <v-btn
            v-else-if="showFollow && followStatus !== 'following'"
            color="primary"
            :variant="followStatus === 'requested' ? 'tonal' : 'flat'"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-account-heart-outline"
            :loading="followBusy"
            @click="handleFollow"
        >{{ followLabel }}</v-btn>
      </div>
      <div v-if="showFollow && followStatus === 'none'" class="tl-sheet-note px-5 pb-4">
        Follow to see their full name and profile, and to message them. They will need to accept.
      </div>
      <div v-else-if="followStatus === 'requested'" class="tl-sheet-note px-5 pb-4">
        Waiting for them to accept. Tap Requested to cancel.
      </div>

      <!-- super admin: impersonate -->
      <div v-if="isSuperUser && !isMe && isGuid(currentItem.userId)" class="px-5 pb-5">
        <v-btn variant="text" block :loading="impersonateLoading" prepend-icon="mdi-login"
               @click="impersonateUser(currentItem.userId)">Impersonate user</v-btn>
      </div>
    </div>
  </v-dialog>
</template>
